import type { Currency, Security } from '../../../../types';
import { ProductTypeEnum } from '../../../../types';

export const CURRENCY_OF_SYMBOL_FILTER_TOOLTIP =
  'View symbols where the Base or Quote currency matches the selected Currency.';

export const BASE_PAIR_FILTER_TOOLTIP =
  'View symbols where the Base and Quote currency matches the selected Pair. For example, filtering on BTC-USD will return BTC-USD Spot, Perps and Futures.';

export type SecurityCurrencyPair = Pick<Security, 'BaseCurrency' | 'QuoteCurrency'>;

export function getBasePairKey({
  BaseCurrency: baseCurrency,
  QuoteCurrency: quoteCurrency,
}: SecurityCurrencyPair): string | undefined {
  return baseCurrency && quoteCurrency ? `${baseCurrency}-${quoteCurrency}` : undefined;
}

export function getCurrencyFilterOptions(currencies: Pick<Currency, 'Symbol'>[]): string[] {
  return currencies.map(currency => currency.Symbol);
}

export function getBasePairFilterOptions(
  securities: Pick<Security, 'BaseCurrency' | 'QuoteCurrency' | 'ProductType'>[]
) {
  const basePairs = new Set<string>();
  for (const security of securities) {
    if (security.ProductType !== ProductTypeEnum.Spot) {
      continue;
    }
    const basePair = getBasePairKey(security);
    if (basePair != null) {
      basePairs.add(basePair);
    }
  }
  return [...basePairs];
}

export function securityMatchesCurrencies(
  security: SecurityCurrencyPair | undefined,
  currencies: string[] | undefined
): boolean {
  if (currencies == null || currencies.length === 0) {
    return true;
  }
  if (security == null) {
    return false;
  }
  const { BaseCurrency: baseCurrency, QuoteCurrency: quoteCurrency } = security;
  return currencies.some(currency => currency === baseCurrency || currency === quoteCurrency);
}

export function securityMatchesBasePairs(
  security: SecurityCurrencyPair | undefined,
  basePairs: string[] | undefined
): boolean {
  if (basePairs == null || basePairs.length === 0) {
    return true;
  }
  if (security == null) {
    return false;
  }
  const basePairKey = getBasePairKey(security);
  return basePairKey != null && basePairs.includes(basePairKey);
}

export function isExcludedByCurrencyOrBasePairFilters(
  symbol: string | undefined,
  securitiesBySymbol: Map<string, SecurityCurrencyPair>,
  currencies: string[] | undefined,
  basePairs: string[] | undefined
): boolean {
  const hasCurrencyFilter = currencies != null && currencies.length > 0;
  const hasBasePairFilter = basePairs != null && basePairs.length > 0;
  if (!hasCurrencyFilter && !hasBasePairFilter) {
    return false;
  }

  const security = symbol != null ? securitiesBySymbol.get(symbol) : undefined;
  return !securityMatchesCurrencies(security, currencies) || !securityMatchesBasePairs(security, basePairs);
}
